import { PasskeyCore } from "@gm-network/passkey";
import { getEvmChainInfo, getAllEvmChainInfos } from "@gm-network/chains";

import { Log } from "./log";
import { GMAccountAbstraction } from "./smart-account";
import {
  GMCore,
  ChainInfo,
  Config,
  ConnectConfig,
  CreateConfig,
  Options,
  defaultSupportedChains,
  AAProvider,
  AccountAbstraction,
  WalletEvent,
  events,
  WindowWithGMNetwork,
  authCoreEvents,
} from "../types";
import { isClient } from "../utils";

interface Emitter {
  emit(event: WalletEvent | string, v: any): void;
  on(eventName: WalletEvent | string, listener: (...args: any[]) => void): void;
  removeListener(
    event: WalletEvent | string,
    listener: (...args: any[]) => void
  ): void;
}

export class InnerCore extends GMCore {
  public readonly isGMNetwork: boolean = true;
  private declare readonly _baseUrl: string;
  private declare readonly _isDev: boolean;
  private declare readonly _config: Config;
  private declare readonly _defaultChainId: number;
  private declare readonly _supportedChainIds: number[];
  private declare readonly _passkey: PasskeyCore;
  private declare _emitter: Emitter;
  private declare _smartAccount: GMAccountAbstraction | null | undefined;
  private _initialized: boolean = false;
  private declare _initializedCallback: () => void;

  constructor(config: Config, options?: Options) {
    super();

    const isDev = options?.debug || false;
    Log._isDev = isDev;
    if (!config.projectId || !config.clientKey || !config.appId) {
      Log.e(
        "GMCore constructor error",
        "Missing required parameters for initialization"
      );
      return;
    }

    this._isDev = isDev;
    this._baseUrl = isDev
      ? "https://test-api-id.gmnetwork.ai"
      : "https://api-id.gmnetwork.ai";
    this._config = config;
    this._defaultChainId = options?.defaultChainId || 1;
    this._supportedChainIds = !!options?.supportedChainIds?.length
      ? options?.supportedChainIds
      : Object.values(defaultSupportedChains);
    this._passkey = new PasskeyCore({ debug: isDev });

    this._initial().catch((e) => Log.e("initial error", e));
  }

  private get _particle() {
    if (isClient()) {
      const res = window as WindowWithGMNetwork;
      if (
        !!res.particle?.wallet &&
        !!res.particle?.status &&
        !!res.particle?.auth
      ) {
        return res;
      }
    }
    return undefined;
  }
  private get _ethereum() {
    return this._particle?.particle?.wallet;
  }
  private get _status() {
    return this._particle?.particle?.status;
  }
  private get _auth() {
    return this._particle?.particle?.auth;
  }

  /**
   * *** private functions ***
   */
  private async _initial(): Promise<void> {
    const EventEmitter = (await import("eventemitter3")).default;
    this._emitter = new EventEmitter();

    if (!this._particle) {
      throw new Error(
        "@gm-network/gmaa initialization parameters error, Use @gm-network/particle ParticleContextProvider to load configuration."
      );
    }

    await this._ethereum?.switchChain(this._defaultChainId);

    if (this._isAuthConnected) {
      await this._createSmartAccount();
    }

    this._status!.on(events.connect, () => {
      Log.d("auth connect");
    });
    this._status!.on(events.disconnect, (v: any) => {
      Log.d("auth disconnect");
      this._smartAccount = null;
      this._emitter.emit(events.disconnect, v);
    });
    this._initialized = true;
    this._initializedCallback?.();
  }

  private async _authLogin(jwt: string) {
    const rsp = await this._auth?.connect({
      provider: "jwt",
      thirdpartyCode: jwt,
    });
    console.log("----- auth connect rsp", rsp);

    return await this._createSmartAccount();
  }

  private async _createSmartAccount() {
    const aa = new GMAccountAbstraction(this._ethereum!);
    const userinfo = await aa
      .connect({
        projectId: this._config.projectId!,
        clientKey: this._config.clientKey!,
        appId: this._config.appId!,
        supportedChainIds: this.supportedChainIds,
      })
      .catch((error) => {
        Log.e("wallet initial error", error);
        return null;
      });
    if (userinfo != null) {
      this._smartAccount = aa;
      this._emitter.emit(events.connect, userinfo);
    } else {
      this._smartAccount = null;
      // this._eventEmitter.emit(events.disconnect, userinfo);
    }
    return userinfo;
  }

  private get _isAuthConnected() {
    if (!this._ethereum) {
      return false;
    }
    return this._ethereum!.provider.isConnected();
  }

  private _throwInitialError(message: string) {
    if (!this._initialized || !this._ethereum) {
      Log.e(
        message,
        "@gm-network/gmaa initialization parameters error, please ensure that the config is correctly set."
      );
      throw new Error(
        "@gm-network/gmaa initialization parameters error, please ensure that the config is correctly set."
      );
    }
  }

  /**
   * *** base config methods ***
   */
  public get wallet(): AccountAbstraction | null | undefined {
    return this._smartAccount;
  }

  public get provider(): AAProvider | null | undefined {
    return this._smartAccount?.provider;
  }

  public get connected(): boolean {
    return this._isAuthConnected && this._smartAccount != null;
  }

  public get initialized(): boolean {
    return this._initialized;
  }

  public setInitializedCallback(callback: () => void) {
    this._initializedCallback = callback;
    if (this._initialized) {
      this._initializedCallback?.();
    }
  }

  public async isAvailable(): Promise<boolean> {
    return await this._passkey.isAvailable();
  }

  public async create(callOptions?: CreateConfig): Promise<boolean> {
    this._throwInitialError("create error");

    try {
      if (this._isAuthConnected) {
        await this.disconnect();
      }
      const jwt = await this._passkey.create(callOptions);

      const user = await this._authLogin(jwt);

      return user != null;
    } catch (e) {
      Log.e("create error", e);
      throw e;
    }
  }

  public async connect(callOptions?: ConnectConfig): Promise<boolean> {
    this._throwInitialError("connect error");

    try {
      let user: any;
      if (!this._isAuthConnected) {
        const jwt = await this._passkey.connect(callOptions);
        user = await this._authLogin(jwt);
      } else if (this._smartAccount == null) {
        user = await this._createSmartAccount();
      }

      return user != null;
    } catch (e) {
      Log.e("connect error", e);
      throw e;
    }
  }

  public async disconnect(): Promise<boolean> {
    this._throwInitialError("disconnect error");

    try {
      if (this._isAuthConnected) {
        await this._ethereum!.provider.disconnect();
      }
      this._smartAccount = null;
      return true;
    } catch (e) {
      Log.e("disconnect error", e);
      throw e;
    }
  }

  public get chainId(): number {
    this._throwInitialError("chainId error");

    return parseInt(this._ethereum!.provider.chainId);
  }
  public get chainInfo(): ChainInfo {
    this._throwInitialError("setChainId error");

    return getEvmChainInfo(this.chainId);
  }

  public setChainId(chainId: number): boolean {
    this._throwInitialError("setChainId error");

    const info = getEvmChainInfo(chainId);

    if (info == null) {
      Log.e("setChainId error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    this._ethereum!.switchChain(chainId).catch((error) =>
      Log.e("setChainId error", error)
    );
    return true;
  }

  public getChainInfo(chainId: number): ChainInfo {
    const info = getEvmChainInfo(chainId);

    if (info == null) {
      Log.e("getChainInfo error", "Unsupported chain");
      throw new Error("Unsupported chain");
    }

    return info;
  }

  public get supportedChainIds(): number[] {
    return this._supportedChainIds;
  }

  public get supportedChains(): ChainInfo[] {
    return getAllEvmChainInfos().filter((v: ChainInfo) =>
      this._supportedChainIds.includes(v.id)
    );
  }

  /**
   * *** common event methods ***
   */
  public on(
    eventName: WalletEvent | string,
    listener: (...args: any[]) => void
  ) {
    this._throwInitialError("on listener");

    const { name, isAuth } = this._resolveListenerName(eventName);

    if (isAuth) {
      this._auth?.events.on(name, listener);
      return;
    }

    this._emitter.on(name, listener);
  }

  public removeListener(
    eventName: WalletEvent | string,
    listener: (...args: any[]) => void
  ) {
    this._throwInitialError("remove listener");

    const { name, isAuth } = this._resolveListenerName(eventName);

    if (isAuth) {
      this._auth?.events.removeListener(name, listener);
      return;
    }

    this._emitter.removeListener(name, listener);
  }

  private _resolveListenerName = (eventName: string) => {
    if (
      (
        [
          authCoreEvents.requestEthereumAccount,
          authCoreEvents.requestSolanaAccount,
          authCoreEvents.masterPasswordChanged,
          authCoreEvents.walletRestored,
        ] as string[]
      ).includes(eventName)
    ) {
      return {
        name: eventName,
        isAuth: true,
      };
    }

    return {
      name:
        authCoreEvents.connect == eventName
          ? events.connect
          : (
              [
                authCoreEvents.disconnect,
                authCoreEvents.userInfoChanged,
              ] as string[]
            ).includes(eventName)
          ? events.disconnect
          : eventName,
    };
  };

  public get key() {
    return (
      this._config.projectId +
      this._config.clientKey +
      this._config.appId
    )
      .replaceAll("-", "")
      .toLowerCase();
  }
}
export default InnerCore;
